import i18next from "i18next";
import {
  ADS_CAMPAIGN_STATUS_ACTIVE,
  ADS_OBJECT_TYPES,
  ALERTS_CAMPAIGNS_TEXT,
  ALERTS_CREATIVE_FATIGUE_TEXT,
  ALERTS_CREATIVE_FATIGUE_TEXT_TABLE,
  CREATIVE_FATIGUE_ALERT_TYPE,
  CREATIVE_FATIGUE_LEVELS,
  YELLOW_ALERT_TYPES,
} from "./constants";

export const getCreativeFatigueLevel = (alert) => {
  const fromField = String(alert?.level || "").toUpperCase();
  switch (fromField) {
    case CREATIVE_FATIGUE_LEVELS.LOW:
    case CREATIVE_FATIGUE_LEVELS.MEDIUM:
    case CREATIVE_FATIGUE_LEVELS.HIGH:
      return fromField;
    default: {
      const match = String(alert?.shortMessage || "").match(
        /Creative Fatigue:\s*(LOW|MEDIUM|HIGH)/i
      );
      return match ? match[1].toUpperCase() : "";
    }
  }
};

export const isCreativeFatigueAlert = (alert) => {
  const type = alert?.type || alert?.alertType;
  return type === CREATIVE_FATIGUE_ALERT_TYPE;
};

export const isVisibleCreativeFatigueAlert = (alert) => {
  if (!isCreativeFatigueAlert(alert)) {
    return true;
  }
  const level = getCreativeFatigueLevel(alert);
  return (
    level === CREATIVE_FATIGUE_LEVELS.MEDIUM ||
    level === CREATIVE_FATIGUE_LEVELS.HIGH
  );
};

export const getCreativeFatigueMessageKey = (alert, variant = "banner") => {
  if (!isVisibleCreativeFatigueAlert(alert)) {
    return "";
  }
  const level = getCreativeFatigueLevel(alert);
  const keys =
    variant === "table"
      ? ALERTS_CREATIVE_FATIGUE_TEXT_TABLE
      : ALERTS_CREATIVE_FATIGUE_TEXT;
  return keys[level] || "";
};

const hasAlertId = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

export const getAlertItemTarget = (alert = {}) => {
  if (hasAlertId(alert.adID)) {
    return { kind: ADS_OBJECT_TYPES.ad, id: String(alert.adID) };
  }
  if (hasAlertId(alert.adGroupID)) {
    return { kind: ADS_OBJECT_TYPES.adGroup, id: String(alert.adGroupID) };
  }
  if (hasAlertId(alert.assetsGroupID)) {
    return {
      kind: ADS_OBJECT_TYPES.assetsGroup,
      id: String(alert.assetsGroupID),
    };
  }

  if (
    alert.entity === ADS_OBJECT_TYPES.ad ||
    alert.entity === ADS_OBJECT_TYPES.adGroup ||
    alert.entity === ADS_OBJECT_TYPES.assetsGroup
  ) {
    return { kind: alert.entity, id: "" };
  }

  return { kind: ADS_OBJECT_TYPES.campaign, id: "" };
};

const isStoredStatusActive = (status) => status === ADS_CAMPAIGN_STATUS_ACTIVE;

export const isAlertForActiveItem = ({
  alert,
  platform,
  states,
  itemStatuses = {},
}) => {
  if (states?.general && states.general !== ADS_CAMPAIGN_STATUS_ACTIVE) {
    return false;
  }

  const platformStatus = Object.prototype.hasOwnProperty.call(
    states || {},
    platform
  )
    ? states[platform]
    : states?.general;

  if (platformStatus !== ADS_CAMPAIGN_STATUS_ACTIVE) {
    return false;
  }

  const target = getAlertItemTarget(alert);

  switch (target.kind) {
    case ADS_OBJECT_TYPES.campaign:
      return true;
    case ADS_OBJECT_TYPES.adGroup:
      return isStoredStatusActive(itemStatuses.adGroup?.[target.id]);
    case ADS_OBJECT_TYPES.assetsGroup:
      return isStoredStatusActive(itemStatuses.assetsGroup?.[target.id]);
    case ADS_OBJECT_TYPES.ad: {
      if (!isStoredStatusActive(itemStatuses.ad?.[target.id])) {
        return false;
      }
      if (!hasAlertId(alert.adGroupID)) {
        return true;
      }
      return isStoredStatusActive(
        itemStatuses.adGroup?.[String(alert.adGroupID)]
      );
    }
    default:
      return false;
  }
};

export const collectAlertItemIds = (campaigns = {}) => {
  const adIDs = new Set();
  const adGroupIDs = new Set();
  const assetsGroupIDs = new Set();

  Object.values(campaigns || {}).forEach((campaign) => {
    const platforms = campaign?.platforms || [];
    const platformsData = campaign?.platformsData || {};

    platforms.forEach((platform) => {
      const alerts = platformsData?.[platform]?.alerts;
      if (!Array.isArray(alerts)) {
        return;
      }

      alerts.forEach((alert) => {
        if (hasAlertId(alert?.adID)) {
          adIDs.add(String(alert.adID));
        }
        if (hasAlertId(alert?.adGroupID)) {
          adGroupIDs.add(String(alert.adGroupID));
        }
        if (hasAlertId(alert?.assetsGroupID)) {
          assetsGroupIDs.add(String(alert.assetsGroupID));
        }
      });
    });
  });

  return {
    adIDs: [...adIDs],
    adGroupIDs: [...adGroupIDs],
    assetsGroupIDs: [...assetsGroupIDs],
  };
};

export const getAlertMessage = (alert, { variant = "banner" } = {}) => {
  const alertType = alert.type || alert.alertType;

  if (alertType === CREATIVE_FATIGUE_ALERT_TYPE) {
    return getCreativeFatigueMessageKey(alert, variant);
  }

  switch (alertType) {
    case "ALERT_DISAPPROVALS_ISSUES":
      return alert.longMessage || alert.shortMessage || "";
    default:
      return (
        ALERTS_CAMPAIGNS_TEXT[alertType] ||
        alert.longMessage ||
        alert.shortMessage ||
        ""
      );
  }
};

export const getRedAlertsFromCampaign = ({
  campaign = {},
  itemStatuses = {},
}) => {
  const platforms = campaign?.platforms || [];
  const platformsData = campaign?.platformsData || {};
  const redAlerts = [];

  platforms.forEach((platform) => {
    const alerts = platformsData?.[platform]?.alerts;
    if (!Array.isArray(alerts) || alerts.length === 0) {
      return;
    }

    const uniqueAlertsByType = new Map();
    alerts.forEach((alert) => {
      const alertType = alert?.type;
      if (!alertType || uniqueAlertsByType.has(alertType)) {
        return;
      }
      if (YELLOW_ALERT_TYPES.includes(alertType)) {
        return;
      }
      if (
        !isAlertForActiveItem({
          alert,
          platform,
          states: campaign?.states,
          itemStatuses,
        })
      ) {
        return;
      }
      const message = getAlertMessage(alert);
      if (!message) {
        return;
      }
      uniqueAlertsByType.set(alertType, {
        platform,
        alertType,
        shortMessage:
          i18next.t(message, { platform: i18next.t(platform) }) || "",
      });
    });

    uniqueAlertsByType.forEach((alertData) => {
      redAlerts.push(alertData);
    });
  });

  return redAlerts;
};

export const getRedAlertsSummaryFromCampaigns = ({
  campaigns = {},
  itemStatuses = {},
}) => {
  const redAlerts = [];
  Object.values(campaigns || {}).forEach((campaign) => {
    const campaignRedAlerts = getRedAlertsFromCampaign({
      campaign,
      itemStatuses,
    });
    if (campaignRedAlerts.length > 0) {
      redAlerts.push(...campaignRedAlerts);
    }
  });

  return {
    hasRedCampaignAlerts: redAlerts.length > 0,
    redCampaignAlerts: redAlerts,
  };
};
